import { useEffect, useRef } from "react"

import useGlobalObjectChecker from "./useGlobalObjectChecker"

interface UseSmartVisibleCrossSellTrackerOptions {
  onViewed: () => void
  enabled?: boolean
  rootMargin?: string
  threshold?: number
}

/**
 * 智能可见性 CrossSellTracker 检查器
 * 1. 首先检查元素是否已在可视区域内，如果在就立即触发
 * 2. 如果不在可视区域内，则等待滚动到可见时触发
 *
 * @param onViewed 元素可见时的回调
 * @param enabled 是否启用检测
 * @param rootMargin 根边距（默认 "0px"）
 * @param threshold 可见阈值（默认 0.1，即 10% 可见）
 * @returns ref 需要绑定到目标元素
 */
const useSmartVisibleCrossSellTracker = ({
  onViewed,
  enabled = true,
  rootMargin = "0px",
  threshold = 0.1,
}: UseSmartVisibleCrossSellTrackerOptions) => {
  const elementRef = useRef<HTMLElement | null>(null)
  const hasTriggeredRef = useRef(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  // 检查元素是否在可视区域内
  const isElementVisible = (element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect()
    const windowHeight = window.innerHeight || document.documentElement.clientHeight
    const windowWidth = window.innerWidth || document.documentElement.clientWidth

    // 计算可见比例
    const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0)
    const visibleWidth = Math.min(rect.right, windowWidth) - Math.max(rect.left, 0)

    if (visibleHeight <= 0 || visibleWidth <= 0) {
      return false
    }

    const elementArea = rect.width * rect.height
    const visibleArea = visibleHeight * visibleWidth
    const visibleRatio = elementArea > 0 ? visibleArea / elementArea : 0

    return visibleRatio >= threshold
  }

  // 当元素可见且 CrossSellTracker 可用时触发
  const handleVisible = () => {
    if (!hasTriggeredRef.current) {
      hasTriggeredRef.current = true
      onViewed()
      // 触发后停止观察
      if (observerRef.current && elementRef.current) {
        observerRef.current.unobserve(elementRef.current)
      }
    }
  }

  // 使用现有的 CrossSellTracker 检查器
  useGlobalObjectChecker({
    globalKey: "CrossSellTracker",
    onReady: () => {
      if (!enabled || !elementRef.current || hasTriggeredRef.current) {
        return
      }

      // 立即检查元素是否可见
      if (isElementVisible(elementRef.current)) {
        handleVisible()
        return
      }

      // 如果不可见，则开始观察
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              handleVisible()
            }
          })
        },
        {
          rootMargin,
          threshold,
        },
      )

      observerRef.current.observe(elementRef.current)
    },
    enabled: enabled && !hasTriggeredRef.current,
  })

  // 清理函数
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [])

  // 重置函数
  const reset = () => {
    hasTriggeredRef.current = false
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
  }

  return { ref: elementRef, reset }
}

export default useSmartVisibleCrossSellTracker
